import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { getAccessToken } from '@neotech-solutions-org/neofusion-fe-shared';
import { ApiError } from '../@types';
import { PaginatedData } from '../@types/api';
import {
  CasinoAuthenticateResponse,
  CasinoCategoriesResponse,
  CasinoBetsPaginatedData,
  CasinoBetsQueryParams,
  CasinoBetsResponse,
  CasinoBetTotals,
  CasinoCategory,
  CasinoCurrency,
  CasinoGameApiItem,
  CasinoGameRow,
  CasinoGamesQueryParams,
  CasinoGamesResponse,
  CasinoMarketplace,
  CasinoMarketplaceEditValues,
  CasinoMarketplaceUpdateResponse,
  CasinoProviderActivationOperation,
  CasinoProviderActivationRequest,
  CasinoProviderApiItem,
  CasinoProvider,
  CasinoProviderSelectableGameType,
  CasinoProviderRow,
  CasinoProvidersListQueryParams,
  CasinoProvidersResponse,
  CasinoRevenueStatistics,
  CasinoStatisticsRequestParams,
  CasinoSummaryPaginatedData,
  CasinoSummaryQueryParams,
  CasinoSummaryResponse,
  CasinoSummaryTotals,
  CasinoTransaction,
  CasinoTransactionRow,
  CasinoTransactionsQueryParams,
  CasinoTransactionsResponse,
  CasinoUser,
  CasinoUserRow,
  CasinoUserStatistics,
  CasinoUsersQueryParams,
  CasinoUsersResponse,
  GgaFreeSpinBetLevelOption,
  GgaFreeSpinBetLevelsResponse,
  AwardGgaFreeSpinsRequest,
  AwardGgaFreeSpinsResponse,
  AwardGgaFreeSpinsBatchRequest,
  AwardGgaFreeSpinsBatchResponse,
  GgaFreeSpinCatalogResponse,
  GgaFreeSpinGameOption,
  GgaFreeSpinHistoryResponse,
  GgaFreeSpinHistoryRow,
  GgaFreeSpinsQueryParams,
  DeleteGgaFreeSpinRequest,
} from '../@types/casino';
import { QUERY_KEYS } from '../constants';
import API_ROUTES from '../constants/apiRoutes';
import { useInvalidateQuery } from '../hooks/useInvalidateQuery';
import { deleteData, getData, patchData, postData } from '../utils/api';

const DEFAULT_PROVIDERS_LIMIT = 100;

const getRequestSource = () => (typeof window === 'undefined' ? undefined : window.location.origin);

export const buildCasinoGamesRequest = (params: CasinoGamesQueryParams) => ({
  offset: (params.page - 1) * params.limit,
  limit: params.limit,
  search: params.search ?? '',
  categoryId: params.categoryId ?? '',
  categoryFilter: params.categoryFilter,
  providerName: params.providerName ?? '',
  ...(params.aggregatorName ? { aggregatorName: [params.aggregatorName] } : {}),
  ...(params.gameType ? { gameType: params.gameType } : {}),
  sortGames: params.sortGames ?? false,
  allDevices: true,
});

export const buildCasinoGamesPath = ({
  sortColumn,
  sortOrder,
}: Pick<CasinoGamesQueryParams, 'sortColumn' | 'sortOrder'> = {}) =>
  sortColumn && sortOrder ? `${API_ROUTES.CASINO.GAMES}?${sortColumn}=${sortOrder}` : API_ROUTES.CASINO.GAMES;

export const buildCasinoUsersRequest = (params: CasinoUsersQueryParams) => ({
  offset: (params.page - 1) * params.limit,
  limit: params.limit,
  username: params.search ?? '',
  entityId: '',
  accessLevel: params.accessLevel ?? '1',
});

export const buildCasinoTransactionsRequest = (params: CasinoTransactionsQueryParams) => ({
  offset: (params.page - 1) * params.limit,
  limit: params.limit,
  dateFrom: params.dateFrom,
  dateTo: params.dateTo,
  ...(params.managerIds ? { managerIds: params.managerIds } : {}),
  ...(params.agentIds ? { agentIds: params.agentIds } : {}),
  ...(params.playerIds ? { playerIds: params.playerIds } : {}),
  roundId: params.roundId ?? '',
  gameName: params.gameName ?? '',
  ...(params.transactionAmount ? { transactionAmount: params.transactionAmount } : {}),
  providerName: params.providerName ?? '',
  transactionType: params.transactionType ?? '',
});

export const buildGgaFreeSpinsHistoryRequest = (params: GgaFreeSpinsQueryParams) => ({
  page: params.page,
  limit: params.limit,
  sortField: params.sortField,
  sortOrder: params.sortOrder,
  sdate: new Date(params.dateFrom).toISOString(),
  edate: new Date(params.dateTo).toISOString(),
  ...(params.search ? { id: params.search } : {}),
  ...(params.playerIds?.length ? { playerIds: params.playerIds.join(',') } : {}),
});

export const buildDeleteGgaFreeSpinPath = ({ freeSpinId, userId, sdate, edate }: DeleteGgaFreeSpinRequest) => {
  const query = new URLSearchParams({ userId, sdate, edate });

  return `${API_ROUTES.CASINO.GGA_FREE_SPINS}/${encodeURIComponent(freeSpinId)}?${query.toString()}`;
};

const convertPercentageFilterToDecimal = (value?: string) => {
  if (!value) return '';

  return Number(value) / 100;
};

export const buildCasinoBetsRequest = (params: CasinoBetsQueryParams) => ({
  offset: (params.page - 1) * params.limit,
  limit: params.limit,
  dateFrom: params.dateFrom,
  dateTo: params.dateTo,
  userId: '',
  entityId: '',
  username: params.username ?? '',
  roundId: params.roundId ?? '',
  providerName: params.providerName ?? '',
  gameId: params.gameId ?? '',
  ...(params.payInAmount ? { payInAmount: params.payInAmount } : {}),
  ...(params.payOutAmount ? { payOutAmount: params.payOutAmount } : {}),
  ...(params.percentageGain ? { percentageGain: convertPercentageFilterToDecimal(params.percentageGain) } : {}),
  ...(params.betStatus ? { betStatus: params.betStatus } : {}),
});

export const buildCasinoSummaryRequest = (params: CasinoSummaryQueryParams) => ({
  offset: (params.page - 1) * params.limit,
  limit: params.limit,
  dateFrom: params.dateFrom,
  dateTo: params.dateTo,
  role: params.role,
  managerIds: params.managerIds,
  agentIds: params.agentIds,
  playerIds: params.playerIds,
  entityId: params.entityId,
  providerName: params.providerName,
  gameName: params.gameName,
  bettingActivity: params.bettingActivity,
});

export const buildCasinoTransactionsPath = ({
  sortColumn = 'createdAt',
  sortOrder = 'desc',
}: Pick<CasinoTransactionsQueryParams, 'sortColumn' | 'sortOrder'> = {}) =>
  `${API_ROUTES.CASINO.TRANSACTIONS}?${sortColumn}=${sortOrder}`;

export const buildCasinoBetsPath = ({
  sortColumn = 'createdAt',
  sortOrder = 'desc',
}: Pick<CasinoBetsQueryParams, 'sortColumn' | 'sortOrder'> = {}) =>
  `${API_ROUTES.CASINO.BETS}?${sortColumn}=${sortOrder}`;

export const buildCasinoUsersPath = ({
  sortColumn = 'username',
  sortOrder = 'asc',
}: Pick<CasinoUsersQueryParams, 'sortColumn' | 'sortOrder'> = {}) =>
  `${API_ROUTES.CASINO.USERS}?${sortColumn}=${sortOrder}`;

export const buildCasinoSummaryPath = ({
  sortColumn = 'username',
  sortOrder = 'asc',
}: Pick<CasinoSummaryQueryParams, 'sortColumn' | 'sortOrder'> = {}) =>
  `${API_ROUTES.CASINO.SUMMARY}?${sortColumn}=${sortOrder}`;

const getCasinoProviderItems = (response: CasinoProvidersResponse): CasinoProviderApiItem[] => {
  return Array.isArray(response) ? response : (response.providers ?? []);
};

const getCasinoGameCategoryName = (game: CasinoGameApiItem) => {
  if (game.categoryName) return game.categoryName;

  return game.categoryNames?.join(', ') ?? '';
};

const getCasinoGameCategoryId = (game: CasinoGameApiItem) => {
  if (game.categoryId) return game.categoryId;

  return game.categoryIds?.[0] ?? '';
};

const getCasinoGamePosition = (game: CasinoGameApiItem) => game.allPosition ?? game.position ?? null;

export const normalizeCasinoGames = (
  response: CasinoGamesResponse,
  params: CasinoGamesQueryParams
): PaginatedData<CasinoGameRow> => ({
  items: (response.games ?? []).map((game) => ({
    ...game,
    id: game._id,
    categoryName: getCasinoGameCategoryName(game),
    categoryId: getCasinoGameCategoryId(game),
    position: getCasinoGamePosition(game),
  })),
  count: response.totalCount ?? 0,
  limit: params.limit,
  page: params.page,
});

const normalizeCasinoProviders = (response: CasinoProvidersResponse): CasinoProvider[] => {
  const providers = getCasinoProviderItems(response);

  return providers.map(({ _id, name }) => ({
    _id,
    name,
  }));
};

export const normalizeGgaFreeSpinOptions = (response: GgaFreeSpinCatalogResponse): GgaFreeSpinGameOption[] => {
  const providerNames = new Map(
    (response.data?.providers ?? []).map((provider) => [provider.ID, provider.name] as const)
  );

  return (response.data?.games ?? []).map((game) => ({
    id: String(game.ID),
    name: game.name,
    providerId: game.provider_id,
    providerName: providerNames.get(game.provider_id),
    image: game.image,
    freeSpinType: game.fs_type,
  }));
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toPositiveNumber = (value: unknown): number | null => {
  const parsedValue = typeof value === 'number' || typeof value === 'string' ? Number(value) : Number.NaN;

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const getFirstPositiveNumber = (record: Record<string, unknown>, keys: string[]): number | null => {
  for (const key of keys) {
    const value = toPositiveNumber(record[key]);
    if (value !== null) return value;
  }

  return null;
};

const formatBetLevelLabel = (level: number, amount?: number | null) =>
  amount !== null && amount !== undefined && amount !== level ? `Level ${level} (${amount})` : `Level ${level}`;

const normalizeBetLevelItem = (item: unknown): GgaFreeSpinBetLevelOption | null => {
  const primitiveLevel = toPositiveNumber(item);
  if (primitiveLevel !== null) return { value: primitiveLevel, label: formatBetLevelLabel(primitiveLevel) };
  if (!isRecord(item)) return null;

  const level = getFirstPositiveNumber(item, ['level', 'betLevel', 'bet_level', 'id', 'ID', 'value', 'amount']);
  if (level === null) return null;

  const amount = getFirstPositiveNumber(item, ['amount', 'betAmount', 'bet_amount', 'bet', 'value']);
  const suppliedLabel = ['label', 'name'].map((key) => item[key]).find((value) => typeof value === 'string');

  return {
    value: level,
    label: suppliedLabel ? String(suppliedLabel) : formatBetLevelLabel(level, amount),
  };
};

const getBetLevelCollection = (response: GgaFreeSpinBetLevelsResponse): unknown => {
  const data = response.data;

  if (Array.isArray(data)) return data;
  if (isRecord(data)) {
    for (const key of ['bets', 'levels', 'betLevels', 'betlevels', 'bet_levels']) {
      if (data[key] !== undefined) return data[key];
    }
    if (['level', 'betLevel', 'bet_level'].some((key) => data[key] !== undefined)) return [data];
  }

  return response.levels;
};

export const normalizeGgaFreeSpinBetLevels = (response: GgaFreeSpinBetLevelsResponse): GgaFreeSpinBetLevelOption[] => {
  const collection = getBetLevelCollection(response);
  let levels: GgaFreeSpinBetLevelOption[] = [];

  if (Array.isArray(collection)) {
    levels = collection.flatMap((item) => {
      const level = normalizeBetLevelItem(item);
      return level ? [level] : [];
    });
  } else if (isRecord(collection)) {
    levels = Object.entries(collection).flatMap(([levelKey, amount]) => {
      const level = toPositiveNumber(levelKey);
      if (level === null) return [];

      return [{ value: level, label: formatBetLevelLabel(level, toPositiveNumber(amount)) }];
    });
  }

  return Array.from(new Map(levels.map((level) => [level.value, level])).values()).sort(
    (left, right) => left.value - right.value
  );
};

const normalizeCasinoCategories = (response: CasinoCategoriesResponse): CasinoCategory[] => {
  return Array.isArray(response) ? response : (response.data ?? []);
};

const getCasinoUserItems = (response: CasinoUsersResponse): CasinoUser[] => response.items ?? response.users ?? [];

const getCasinoTransactionItems = (response: CasinoTransactionsResponse): CasinoTransaction[] =>
  response.transactions ?? [];

export const normalizeCasinoUsers = (
  response: CasinoUsersResponse,
  params: CasinoUsersQueryParams
): PaginatedData<CasinoUserRow> => {
  const users = getCasinoUserItems(response);

  return {
    items: users.map((user) => ({
      ...user,
      id: user._id,
    })),
    count: response.totalCount ?? response.count ?? users.length,
    limit: params.limit,
    page: params.page,
  };
};

export const normalizeGgaFreeSpinsHistory = (
  response: GgaFreeSpinHistoryResponse,
  params: GgaFreeSpinsQueryParams
): PaginatedData<GgaFreeSpinHistoryRow> => ({
  items: (response.data?.users ?? []).map((freeSpin) => ({
    ...freeSpin,
    id: String(freeSpin.id),
    status: freeSpin.status === 'S' ? 'Successful' : freeSpin.status,
  })),
  count: response.data?.pagination?.totalCount ?? 0,
  limit: response.data?.pagination?.per_page ?? params.limit,
  page: response.data?.pagination?.page ?? params.page,
});

export const normalizeCasinoTransactions = (
  response: CasinoTransactionsResponse,
  params: CasinoTransactionsQueryParams
): PaginatedData<CasinoTransactionRow> => {
  const transactions = getCasinoTransactionItems(response);

  return {
    items: transactions.map((transaction) => ({
      ...transaction,
      id: transaction._id,
    })),
    count: response.totalCount ?? transactions.length,
    limit: params.limit,
    page: params.page,
  };
};

const DEFAULT_CASINO_BET_TOTALS: CasinoBetTotals = {
  totalPayInAmount: 0,
  totalPayOutAmount: 0,
  totalPercentageGain: 0,
};

const DEFAULT_CASINO_SUMMARY_TOTALS: CasinoSummaryTotals = {
  totalDistributedBonusAmount: 0,
  jackpotAmount: 0,
  ngr: 0,
  totalPaidInAmount: 0,
  totalPaidOutAmount: 0,
  totalProfit: 0,
};

export const normalizeCasinoBets = (
  response: CasinoBetsResponse,
  params: CasinoBetsQueryParams
): CasinoBetsPaginatedData => {
  const bets = response.items ?? [];

  return {
    items: bets.map((bet) => ({
      ...bet,
      id: bet._id,
    })),
    count: response.count ?? bets.length,
    limit: params.limit,
    page: params.page,
    totals: {
      ...DEFAULT_CASINO_BET_TOTALS,
      ...(response.totals ?? {}),
    },
  };
};

export const normalizeCasinoSummary = (
  response: CasinoSummaryResponse,
  params: CasinoSummaryQueryParams
): CasinoSummaryPaginatedData => {
  const items = response.items ?? [];
  const totals = response.totals ?? {};

  return {
    items: items.map((item, index) => ({
      ...item,
      totalDistributedBonusAmount: item.totalDistributedBonusAmount ?? 0,
      jackpotAmount: item.jackpotAmount ?? 0,
      id: item.user?.clientUserId ?? item.user?._id ?? `summary-${index}`,
    })),
    count: response.count ?? items.length,
    limit: params.limit,
    page: params.page,
    totals: {
      ...DEFAULT_CASINO_SUMMARY_TOTALS,
      ...totals,
      totalDistributedBonusAmount: totals.totalDistributedBonusAmount ?? 0,
      jackpotAmount: totals.jackpotAmount ?? 0,
    },
  };
};

export const useCasinoGames = (params: CasinoGamesQueryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoGames, params],

    queryFn: async (): Promise<PaginatedData<CasinoGameRow>> => {
      const response = await postData<CasinoGamesResponse>(
        buildCasinoGamesPath(params),
        buildCasinoGamesRequest(params),
        'casino'
      );

      return normalizeCasinoGames(response, params);
    },

    placeholderData: keepPreviousData,
  });
};

export const useCasinoProviders = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoProviders],

    queryFn: async (): Promise<CasinoProvider[]> => {
      const response = await postData<CasinoProvidersResponse>(
        API_ROUTES.CASINO.PROVIDERS,
        {
          name: '',
          offset: 0,
          limit: DEFAULT_PROVIDERS_LIMIT,
        },
        'casino'
      );

      return normalizeCasinoProviders(response);
    },
  });
};

const getCasinoProvidersCount = (
  response: CasinoProvidersResponse,
  providers: CasinoProviderApiItem[],
  params: CasinoProvidersListQueryParams
) => {
  if (!Array.isArray(response) && typeof response.totalCount === 'number') {
    return response.totalCount;
  }

  if (Array.isArray(response)) {
    return providers.length;
  }

  const visibleProviderCount = (params.page - 1) * params.limit + providers.length;

  return providers.length >= params.limit ? visibleProviderCount + 1 : visibleProviderCount;
};

const CASINO_PROVIDER_SORT_FIELDS: Record<string, string> = {
  name: 'providerName',
  aggregatorName: 'aggregatorName',
  gameType: 'gameType',
  position: 'position',
  status: 'status',
};

export const buildCasinoActiveAggregatorProvidersRequest = ({
  page,
  limit,
  name,
  sortColumn,
  sortOrder,
}: CasinoProvidersListQueryParams) => {
  const apiSortColumn = sortColumn ? CASINO_PROVIDER_SORT_FIELDS[sortColumn] : undefined;

  return {
    offset: (page - 1) * limit,
    limit,
    ...(name ? { name } : {}),
    ...(sortOrder && apiSortColumn ? { [apiSortColumn]: sortOrder } : {}),
  };
};

const getCasinoActiveAggregatorProvidersCount = (
  response: CasinoProvidersResponse,
  providers: CasinoProviderApiItem[]
) => (!Array.isArray(response) && typeof response.totalCount === 'number' ? response.totalCount : providers.length);

const toCasinoProviderRows = (providers: CasinoProviderApiItem[]): CasinoProviderRow[] =>
  providers.map((provider) => ({
    ...provider,
    id: provider._id,
  }));

export const normalizeCasinoProvidersList = (
  response: CasinoProvidersResponse,
  params: CasinoProvidersListQueryParams
): PaginatedData<CasinoProviderRow> => {
  const providers = getCasinoProviderItems(response);

  return {
    items: toCasinoProviderRows(providers),
    count: getCasinoProvidersCount(response, providers, params),
    limit: params.limit,
    page: params.page,
  };
};

export const normalizeCasinoActiveAggregatorProvidersList = (
  response: CasinoProvidersResponse,
  params: CasinoProvidersListQueryParams
): PaginatedData<CasinoProviderRow> => {
  const providers = getCasinoProviderItems(response);

  return {
    items: toCasinoProviderRows(providers),
    count: getCasinoActiveAggregatorProvidersCount(response, providers),
    limit: params.limit,
    page: params.page,
  };
};

export const useCasinoProvidersList = (params: CasinoProvidersListQueryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoProvidersList, 'legacy', params],

    queryFn: async (): Promise<PaginatedData<CasinoProviderRow>> => {
      const response = await postData<CasinoProvidersResponse>(
        API_ROUTES.CASINO.PROVIDERS,
        {
          name: params.name ?? '',
          offset: (params.page - 1) * params.limit,
          limit: params.limit,
        },
        'casino'
      );

      return normalizeCasinoProvidersList(response, params);
    },

    placeholderData: keepPreviousData,
  });
};

export const useCasinoActiveAggregatorProvidersList = (params: CasinoProvidersListQueryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoProvidersList, 'active-aggregator', params],

    queryFn: async (): Promise<PaginatedData<CasinoProviderRow>> => {
      const response = await getData<CasinoProvidersResponse>(
        API_ROUTES.CASINO.ACTIVE_AGGREGATOR_PROVIDERS,
        buildCasinoActiveAggregatorProvidersRequest(params),
        'casino'
      );

      return normalizeCasinoActiveAggregatorProvidersList(response, params);
    },

    placeholderData: keepPreviousData,
  });
};

export const buildCasinoProviderActivationRequest = ({
  name,
  aggregatorName,
  providerId,
  gameType,
  operation,
}: {
  name: string;
  aggregatorName: string;
  providerId?: string;
  gameType?: CasinoProviderSelectableGameType;
  operation: CasinoProviderActivationOperation;
}): CasinoProviderActivationRequest => ({
  providers: [
    {
      name,
      aggregatorName,
      ...(providerId ? { providerId } : {}),
      ...(gameType ? { gameType } : {}),
      operation,
    },
  ],
});

export const useUpdateCasinoActiveProviders = () => {
  const invalidateData = useInvalidateQuery();

  return useMutation<CasinoMarketplace, ApiError, CasinoProviderActivationRequest>({
    mutationFn: (data: CasinoProviderActivationRequest): Promise<CasinoMarketplace> =>
      patchData<CasinoMarketplace>(API_ROUTES.CASINO.MARKETPLACE_ACTIVE_PROVIDERS, data, 'casino'),
    onSuccess: () => {
      invalidateData([
        QUERY_KEYS.casinoProvidersList,
        QUERY_KEYS.casinoProviders,
        QUERY_KEYS.casinoGames,
        QUERY_KEYS.casinoMarketplace,
      ]);
    },
  });
};

export const useCasinoCategories = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoCategories],

    queryFn: async (): Promise<CasinoCategory[]> => {
      const response = await getData<CasinoCategoriesResponse>(API_ROUTES.CASINO.CATEGORIES, undefined, 'casino');

      return normalizeCasinoCategories(response);
    },
  });
};

export const useCasinoCurrencies = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoCurrencies],

    queryFn: (): Promise<CasinoCurrency[]> =>
      getData<CasinoCurrency[]>(API_ROUTES.CASINO.CURRENCIES, undefined, 'casino'),
  });
};

export const getCasinoAuthenticatedMarketplace = async (): Promise<CasinoMarketplace> => {
  const token = await getAccessToken();
  const response = await postData<CasinoAuthenticateResponse>(API_ROUTES.CASINO.AUTHENTICATE, { token }, 'casino', {
    source: getRequestSource(),
  });

  if (!response.marketplace) {
    throw new Error('Casino authentication did not return marketplace data.');
  }

  return response.marketplace;
};

export const useCasinoMarketplace = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoMarketplace],
    queryFn: getCasinoAuthenticatedMarketplace,
    enabled,
  });
};

export const useGgaFreeSpinOptions = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoGgaFreeSpinOptions],
    queryFn: async (): Promise<GgaFreeSpinGameOption[]> => {
      const response = await getData<GgaFreeSpinCatalogResponse>(
        API_ROUTES.CASINO.GGA_FREE_SPIN_OPTIONS,
        undefined,
        'casino'
      );

      return normalizeGgaFreeSpinOptions(response);
    },
    enabled,
  });
};

export const useGgaFreeSpinBetLevels = (
  gameId: string | number | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoGgaFreeSpinBetLevels, gameId],
    queryFn: async (): Promise<GgaFreeSpinBetLevelOption[]> => {
      const response = await getData<GgaFreeSpinBetLevelsResponse>(
        API_ROUTES.CASINO.GGA_FREE_SPIN_BET_LEVELS,
        { gameId: Number(gameId) },
        'casino'
      );

      return normalizeGgaFreeSpinBetLevels(response);
    },
    enabled: enabled && !!gameId,
  });
};

type UpdateCasinoMarketplaceParams = {
  marketplaceId: string;
  data: CasinoMarketplaceEditValues;
};

export const useUpdateCasinoMarketplace = () => {
  const invalidateData = useInvalidateQuery();

  return useMutation<CasinoMarketplaceUpdateResponse, ApiError, UpdateCasinoMarketplaceParams>({
    mutationFn: ({ marketplaceId, data }: UpdateCasinoMarketplaceParams): Promise<CasinoMarketplaceUpdateResponse> =>
      patchData<CasinoMarketplaceUpdateResponse>(
        `${API_ROUTES.CASINO.MARKETPLACE_UPDATE}/${marketplaceId}`,
        data,
        'casino'
      ),
    onSuccess: () => {
      invalidateData([QUERY_KEYS.casinoMarketplace]);
    },
  });
};

export const useCasinoUsers = (params: CasinoUsersQueryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoUsers, params],

    queryFn: async (): Promise<PaginatedData<CasinoUserRow>> => {
      const response = await postData<CasinoUsersResponse>(
        buildCasinoUsersPath(params),
        buildCasinoUsersRequest(params),
        'casino'
      );

      return normalizeCasinoUsers(response, params);
    },

    placeholderData: keepPreviousData,
  });
};

export const useAwardGgaFreeSpins = () => {
  const invalidateData = useInvalidateQuery();

  return useMutation<AwardGgaFreeSpinsResponse, ApiError, AwardGgaFreeSpinsRequest>({
    mutationFn: (data: AwardGgaFreeSpinsRequest): Promise<AwardGgaFreeSpinsResponse> =>
      postData<AwardGgaFreeSpinsResponse>(API_ROUTES.CASINO.GGA_FREE_SPINS, data, 'casino'),
    onSuccess: () => invalidateData([QUERY_KEYS.casinoGgaFreeSpins]),
  });
};

export const useAwardGgaFreeSpinsBatch = () => {
  const invalidateData = useInvalidateQuery();

  return useMutation<AwardGgaFreeSpinsBatchResponse, ApiError, AwardGgaFreeSpinsBatchRequest>({
    mutationFn: (data): Promise<AwardGgaFreeSpinsBatchResponse> =>
      postData<AwardGgaFreeSpinsBatchResponse>(API_ROUTES.CASINO.GGA_FREE_SPINS_BATCH, data, 'casino'),
    onSuccess: () => invalidateData([QUERY_KEYS.casinoGgaFreeSpins]),
  });
};

export const useGgaFreeSpinsHistory = (params: GgaFreeSpinsQueryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoGgaFreeSpins, params],
    queryFn: async (): Promise<ReturnType<typeof normalizeGgaFreeSpinsHistory>> => {
      const response = await getData<GgaFreeSpinHistoryResponse>(
        API_ROUTES.CASINO.GGA_FREE_SPINS,
        buildGgaFreeSpinsHistoryRequest(params),
        'casino'
      );

      return normalizeGgaFreeSpinsHistory(response, params);
    },
    placeholderData: keepPreviousData,
  });
};

export const useDeleteGgaFreeSpin = () => {
  const invalidateData = useInvalidateQuery();

  return useMutation<unknown, ApiError, DeleteGgaFreeSpinRequest>({
    mutationFn: (request) => deleteData(buildDeleteGgaFreeSpinPath(request), 'casino'),
    onSuccess: () => invalidateData([QUERY_KEYS.casinoGgaFreeSpins]),
  });
};

export const useCasinoTransactions = (params: CasinoTransactionsQueryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoTransactions, params],

    queryFn: async (): Promise<PaginatedData<CasinoTransactionRow>> => {
      const response = await postData<CasinoTransactionsResponse>(
        buildCasinoTransactionsPath(params),
        buildCasinoTransactionsRequest(params),
        'casino'
      );

      return normalizeCasinoTransactions(response, params);
    },

    placeholderData: keepPreviousData,
  });
};

export const useCasinoBets = (params: CasinoBetsQueryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoBets, params],

    queryFn: async (): Promise<CasinoBetsPaginatedData> => {
      const response = await postData<CasinoBetsResponse>(
        buildCasinoBetsPath(params),
        buildCasinoBetsRequest(params),
        'casino'
      );

      return normalizeCasinoBets(response, params);
    },

    placeholderData: keepPreviousData,
  });
};

export const useCasinoRevenueStatistics = (params: CasinoStatisticsRequestParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoRevenueStatistics, params],

    queryFn: (): Promise<CasinoRevenueStatistics> =>
      postData<CasinoRevenueStatistics>(API_ROUTES.CASINO.STATISTICS_REVENUE, params, 'casino'),

    placeholderData: keepPreviousData,
  });
};

export const useCasinoUserStatistics = (params: CasinoStatisticsRequestParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoUserStatistics, params],

    queryFn: (): Promise<CasinoUserStatistics> =>
      postData<CasinoUserStatistics>(API_ROUTES.CASINO.STATISTICS_USERS, params, 'casino'),

    placeholderData: keepPreviousData,
  });
};

export const useCasinoSummary = (params: CasinoSummaryQueryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.casinoSummary, params],

    queryFn: async (): Promise<CasinoSummaryPaginatedData> => {
      const response = await postData<CasinoSummaryResponse>(
        buildCasinoSummaryPath(params),
        buildCasinoSummaryRequest(params),
        'casino'
      );

      return normalizeCasinoSummary(response, params);
    },

    placeholderData: keepPreviousData,
  });
};
